import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SkinAnalysisPredictValidationClient } from '@/infrastructure/SkinAnalysisPredictValidationClient';
import { getConfig, Stage } from '@/config';
import type {
  SkinAnalysisPredictValidationRequest,
  SkinAnalysisPredictValidationResponse,
} from '@/models/SkinAnalysisPredictValidation';

export const useSkinAnalysisPredictValidation = () => {
  const client = useMemo(() => new SkinAnalysisPredictValidationClient(), []);
  const comparisonEnabled = [Stage.LOCAL, Stage.DEV, Stage.PROD].includes(getConfig().stage);
  const [geminiLatencyMs, setGeminiLatencyMs] = useState<number | null>(null);
  const [sonnetLatencyMs, setSonnetLatencyMs] = useState<number | null>(null);
  const gemini = useMutation<SkinAnalysisPredictValidationResponse, Error, SkinAnalysisPredictValidationRequest>({
    mutationFn: async (request) => {
      const startedAt = performance.now();
      try {
        return await client.predict(request);
      } finally {
        setGeminiLatencyMs(Math.round(performance.now() - startedAt));
      }
    },
  });
  const sonnet = useMutation<SkinAnalysisPredictValidationResponse, Error, SkinAnalysisPredictValidationRequest>({
    mutationFn: async (request) => {
      const startedAt = performance.now();
      try {
        return await client.predictSonnet(request);
      } finally {
        setSonnetLatencyMs(Math.round(performance.now() - startedAt));
      }
    },
  });
  const { reset: resetGemini } = gemini;
  const { reset: resetSonnet } = sonnet;

  const clearResult = useCallback(() => {
    resetGemini();
    resetSonnet();
    setGeminiLatencyMs(null);
    setSonnetLatencyMs(null);
  }, [resetGemini, resetSonnet]);

  const predict = useCallback(
    async (request: SkinAnalysisPredictValidationRequest): Promise<void> => {
      clearResult();
      if (!comparisonEnabled) {
        await Promise.allSettled([gemini.mutateAsync(request)]);
        return;
      }
      await Promise.allSettled([gemini.mutateAsync(request), sonnet.mutateAsync(request)]);
    },
    [clearResult, comparisonEnabled, gemini, sonnet],
  );

  return {
    predict,
    clearResult,
    comparisonEnabled,
    isLoading: gemini.isPending || sonnet.isPending,
    gemini: {
      result: gemini.data ?? null,
      isLoading: gemini.isPending,
      error: gemini.error?.message ?? null,
      lastLatencyMs: geminiLatencyMs,
    },
    sonnet: {
      result: sonnet.data ?? null,
      isLoading: sonnet.isPending,
      error: sonnet.error?.message ?? null,
      lastLatencyMs: sonnetLatencyMs,
    },
  };
};
